<script setup>
import { useGtm } from '@/composables/helpers/client-only/gtm';

/*
  This component accepts the following colors: white, solaris, meteor;
  If "useX" is set to true, an "X" will appear next to the text.
  If useHover is set to true, hover states will be active. Otherwise, hovering will do nothing.
  All logic should for clicks should be handled on the parent level.
*/

const props = defineProps({
    link: {
        type: [Boolean, String],
        required: false,
        default: false,
    },
    span: {
        type: Boolean,
        required: false,
        default: false,
    },
    text: {
        type: String,
        required: true,
    },
    colorSet: {
        type: String,
        required: false,
        default: 'white',
    },
    useHover: {
        type: Boolean,
        required: false,
        default: false,
    },
    useX: {
        type: Boolean,
        required: false,
        default: false,
    },
    replace: {
        type: Boolean,
        required: false,
        default: false,
    },
    ariaText: {
        type: String,
    },
    textOnly: {
        type: Boolean,
        required: false,
        default: false,        
    },
    useA: {
        type: Boolean,
        required: false,
        default: false,
    },
    gtmEventName: {
        type: String,
        required: false,
        default: 'tag_clicked',
    },
    gtmEventOptions: {
        type: [Object],
        required: false,
        default: {
            category: 'custom_events',
            action: 'click',
        },
    },
    capitalize: {
        type: Boolean,
        required: false,
        default: false,
    },
    category: {
        type: Boolean,
        required: false,
        default: false,
    }
});

const handleGtmEvent = () => {
    const excludedLabels = ['Clear All Filters'];
    const shouldExclude = excludedLabels.includes(props.text);
    if (shouldExclude) return;
    const name = props.gtmEventName;
    const options = props.gtmEventOptions;
    options.label = props.text;
    useGtm(name, options);
};

</script>

<template>
    <MessLink v-if="link && !useA" :href="link" :replace="replace" class="project-tag"
        :class="[colorSet, { useHover: useHover, empty: textOnly, 'category': category, 'useX': useX }]" @click="handleGtmEvent()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <MessSvg v-if="useX" type="tagx" />
    </MessLink>

    <a v-else-if="link && useA" :href="link" class="mess-link not-mess-link project-tag"
        :aria-label="ariaText" 
        :class="[colorSet, { useHover: useHover, 'useX': useX }]" @click="handleGtmEvent()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <MessSvg v-if="useX" type="tagx" />
    </a>

    <span v-else-if="span" class="project-tag" :class="[colorSet, { useHover: useHover, 'useX': useX  }]" @click="handleGtmEvent()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <MessSvg v-if="useX" type="tagx" />
    </span>

    <button v-else class="project-tag" :class="[colorSet, { useHover: useHover, 'useX': useX }]" @click="handleGtmEvent()">
        <slot></slot>
        <p class="caps fs-12">
            {{ text }}
            <span v-if="ariaText" class="sr-only">{{ ariaText }}</span>
            <span></span>
        </p>
        <MessSvg v-if="useX" type="tagx" />
    </button>
</template>

<style lang="scss">

a.project-tag {
    cursor: pointer;
}

.project-tag {
    display: inline-flex;
    color: var(--color);
    background-color: var(--secondColor);
    padding: 0.375rem 0.625rem;
    border: 1px solid var(--color);
    border-radius: var(--radius);
    transition: 0.33s ease;
    font-size: 0.6875rem;
    letter-spacing: 0.0375rem;
    line-height: 0.034375rem;
    font-weight: 400;
    cursor: default;

    &.category {
        padding: 0.375rem 0.625rem;
        letter-spacing: 0.05625rem;

        .fs-12 {
            letter-spacing: 0.05625rem;
        }
    }

    &.useHover:hover {
        cursor: pointer;
        background-color: var(--color);
        border: 1px solid var(--secondColor);
        color: var(--secondColor);

        &.transparent {
            color: var(--Chapati-500);
        }
    }

    &.empty {
        padding: 0;
        border: none;
        border-radius: 0;
        border-bottom: 1px solid var(--color);
        
        &.useHover:hover {
            border: none;
            border-radius: 0;
            background-color: transparent;
            color: var(--orange);
            border-bottom: 1px solid var(--orange);
        }
    }

    &.white {
        --color: var(--teal);
        --secondColor: white;
        border-color: white;

        .mess-svg {
            path { fill: var(--teal-light); }
        }
    }

    &.white-borderless {
        --color: var(--Neutrals-Meteor);
        --secondColor: var(--Neutrals-White);
        border-color: white;
    }

    &.transparent {
        --color: var(--Neutrals-Meteor);
        --secondColor: var(--Neutrals-White);
        background: transparent;
    }

    &.meteor {
        --color: var(--Neutrals-White);
        --secondColor: var(--Neutrals-Meteor);
    }

    &.meteor-transparent {
        --color: var(--teal-gray);
        --secondColor: var(--orange-dark);
        background: transparent;
    }

    &.solaris {
        border-color: var(--orange-dark);
        --color: var(--orange-dark);
        --secondColor: transparent;

        &:hover {
            --color: var(--cream);
            --secondColor: var(--orange);
        }
    }

    &.berlin-bus {
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Berlin-Bus-Medium);
        border: 1px solid transparent;

        &:hover {
            border: 1px solid var(--Neutrals-Eclipse);
        }
    }

    &.berlin-bus-original {
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Berlin-Bus-Original);
        border: 1px solid transparent;

        &:hover {
            border: 1px solid var(--Neutrals-Eclipse);
        }
    }

    &.cherry-blossom {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Cherry-Blossom-Light);

        &:hover {
            --secondColor: white;
        }
    }

    &.cherry-blossom-alt {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Cherry-Blossom-Alt-Cherry-Blossom-Mid);

        &:hover {
            --secondColor: white;
        }
    }

    &.solaris-alt {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Solaris-Alt-Solaris-Mid);

        &:hover {
            --secondColor: white;
        }
    }

    &.calypso-alt {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Calypso-Alt-Calypso-Mid);

        &:hover {
            --secondColor: white;
        }
    }

    &.neutrals-silver {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Neutrals-Silver);

        &:hover {
            --secondColor: white;
        }
    }

    &.neutrals-milky-way {
        border-color: var(--Neutrals-Milky-Way);
        --color: var(--Neutrals-Eclipse);
        --secondColor: transparent;

        &:hover {
            --secondColor: white;
        }
    }

    &.oz-original {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Oz-Original);

        &:hover {
            --secondColor: white;
        }
    }

    &.calypso-light {
        border-color: var(--secondColor);
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Calypso-Light);

        &.useHover:hover {
            --secondColor: white;
        }
    }

    &.transparent {
        --secondColor: var(--Neutrals-Eclipse);
        --color: var(--Neutrals-Eclipse);
        background-color: transparent;
    }

    &.transparent-meteor {
        --secondColor: var(--Neutrals-White);
        --color: var(--Neutrals-Meteor);
        background-color: transparent;
    }

    &.indigo {
        --color: var(--Neutrals-Eclipse);
        --secondColor: var(--Indigo);
        border-color: var(--Indigo);

        &.useHover:hover {
            --secondColor: white;
        }
    }

    &.blank {
        --color: var(--teal-gray);
        border-color: transparent;
        background-color: transparent;
        text-decoration: underline;
        text-underline-offset: 0.25rem;

        &.useHover:hover {
            --secondColor: var(--teal);
            border-color: transparent;
            background-color: transparent;
        }
    }

    &.teal {
        --color: var(--teal-gray);
        --secondColor: transparent;
        border-color: var(--teal-gray);

        &.useHover:hover {
            --secondColor: white;
        }
    }

    &.orange {
        --color: var(--orange-dark);
        --secondColor: transparent;
        border-color: var(--orange-dark);

        &.useHover:hover {
            --secondColor: white;
            --color: var(--orange-dark);
        }
    }

    .mess-svg {
        align-self: center;
        margin-left: 0.75rem;

        &.margin-right {
            margin-right: 0.75rem;
            margin-left: 0;
        }
    }
}
</style>
